export type WikiTextBlock = {
  id: string;
  type: 'text';
  text: string;
};

export type WikiImageBlock = {
  id: string;
  type: 'image';
  url: string;
  caption: string;
};

export type WikiContentBlock = WikiTextBlock | WikiImageBlock;

let blockSequence = 0;

function blockId() {
  blockSequence += 1;
  return `wiki-block-${Date.now()}-${blockSequence}`;
}

export function createWikiTextBlock(text = ''): WikiTextBlock {
  return { id: blockId(), type: 'text', text };
}

export function createWikiImageBlock(url = '', caption = ''): WikiImageBlock {
  return { id: blockId(), type: 'image', url, caption };
}

export function parseWikiContent(content: string): WikiContentBlock[] {
  try {
    const parsed = JSON.parse(content) as {
      version?: unknown;
      blocks?: Array<Record<string, unknown>>;
    };

    if (parsed.version === 1 && Array.isArray(parsed.blocks)) {
      const blocks = parsed.blocks.flatMap((block): WikiContentBlock[] => {
        if (block.type === 'text' && typeof block.text === 'string') {
          return [createWikiTextBlock(block.text)];
        }
        if (block.type === 'image' && typeof block.url === 'string') {
          return [
            createWikiImageBlock(
              block.url,
              typeof block.caption === 'string' ? block.caption : '',
            ),
          ];
        }
        return [];
      });
      if (blocks.length > 0) return blocks;
    }
  } catch {
    // Старые статьи хранятся обычным текстом — превращаем его в один текстовый блок.
  }

  return [createWikiTextBlock(content)];
}

export function serializeWikiContent(blocks: WikiContentBlock[]): string {
  return JSON.stringify({
    version: 1,
    blocks: blocks.map((block) =>
      block.type === 'text'
        ? { type: 'text', text: block.text }
        : { type: 'image', url: block.url, caption: block.caption },
    ),
  });
}

export function isWikiImageUrl(value: string): boolean {
  const url = value.trim();
  return /^https?:\/\//.test(url) || url.startsWith('/');
}

export function hasWikiContent(blocks: WikiContentBlock[]): boolean {
  return blocks.some((block) =>
    block.type === 'text' ? block.text.trim().length > 0 : isWikiImageUrl(block.url),
  );
}
